import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useApiClientWrapper, useMasterData } from 'hooks';
import { createFormThunk, updateFormThunk } from '@spike/forms-action';
import { Option } from '@spike/model';
import { type SaveForm, SentVia, SendCondition } from '@spike/form-model';
import { showErrorThunk } from '@spike/notifications-action';
import { OverFullWindow } from 'components/UI';
import { Form, FormField, FieldOption, isAutomaticSendCondition } from 'model/Forms';
import FormSendingRules from './FormSendingRules';
import FormServiceSelector, { FormServiceCategory } from './FormServiceSelector';
import { Input } from 'components/UI/V3/Forms/Input/Input';
import Button from 'components/UI/V3/Button/Button';
import Spinner from 'components/UI/V3/Spinner/Spinner';
import styles from './AddEditForm.module.scss';
import { faAlignLeft, faChevronUp, faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import ExitWithoutSavingModal from 'components/UI/V3/Modals/ExitWithoutSavingModal';
import { GenericDeleteModal } from 'components/UI/V3/Modals/DeleteModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCopy,
    faTrashAlt,
    faCheckSquare,
    faSquareCheck,
    faCircleDot,
    faEquals,
    faPlus
} from '@fortawesome/pro-regular-svg-icons';
import { Switch } from 'components/UI/V3/Forms/Switch/Switch';
import SuccessScreen from 'components/UI/V3/SuccessScreen';
import { Textarea } from 'components/UI/V3/Forms/Textarea/Textarea';
import { Dropdown } from 'components/UI/V3/Dropdown';

interface Props {
    isActive: boolean;
    form: Form;
    mode?: 'add' | 'edit' | 'view';
    isLoading?: boolean; // loading details for edit/view
    assignServiceId?: number;
    serviceCategory?: 'grooming' | 'daycare' | 'boarding' | 'transportation' | 'walking' | 'training';
    onClose: () => void;
    onSaved: (form: Form) => void;
}

const MAX_OPTIONS_PER_QUESTION = 10;

const associationsFromForm = (f: Form) => {
    const services = f.services || [];
    const serviceIds =
        f.serviceIds && f.serviceIds.length > 0
            ? f.serviceIds.map(Number).filter(id => Number.isFinite(id) && id > 0)
            : services.map(service => Number(service.id)).filter(id => Number.isFinite(id) && id > 0);

    return { services, serviceIds };
};

// Normalize any incoming form shape (from API details) to our editing shape with `fields`
const normalizeIncomingForm = (f: Form, mode: 'add' | 'edit' | 'view'): Form => {
    // Si ya está en forma editable, no recalcular preguntas; sí alinear servicios
    if (Array.isArray(f.fields) && f.fields.length) {
        return {
            ...f,
            ...associationsFromForm(f),
            sendCondition: f.sendCondition || SendCondition.Manually
        };
    }

    const questions = (f as any).customQuestions ?? []; // ✅ único origen

    const fields: FormField[] = questions
        .slice()
        .sort((a: any, b: any) => (a?.displayOrder ?? 0) - (b?.displayOrder ?? 0))
        .map((q: any, idx: number) => {
            const questionType = (q?.answerType ?? 'short_answer') as FormField['answerType'];
            const type = answerTypeToFieldType[questionType as AnswerTypeId] ?? 'text';

            const rawOptions = (q?.options ?? []) as any[];
            const options =
                type === 'radio' || type === 'checkbox'
                    ? rawOptions
                          .map((o: any, j: number) => ({
                              id: o?.id,
                              label: String(o?.label ?? '').trim(),
                              value: o?.value,
                              position: typeof o?.position === 'number' ? o.position : j,
                              active: o?.active ?? true
                          }))
                          .filter((o: any) => o.label.length > 0)
                    : undefined;

            return {
                id: String(q?.id ?? `field_${Date.now()}_${idx}`),
                type,
                label: q?.questionText ?? '',
                required: Boolean(q?.required),
                answerType: q?.answerType,
                category: q?.category ?? 'for_pets',
                ...(options ? { options } : {})
            } as FormField;
        });

    const { services, serviceIds } = associationsFromForm(f);

    return {
        ...f,
        fields,
        sendCondition: f.sendCondition || SendCondition.Manually,
        services,
        serviceIds
    };
};

type AnswerTypeId = 'short_answer' | 'long_answer' | 'single_choice' | 'multiple_choice';

const fieldTypeToAnswerType: Partial<Record<FormField['type'], AnswerTypeId>> = {
    text: 'short_answer',
    textarea: 'long_answer',
    radio: 'single_choice',
    checkbox: 'multiple_choice'
};

const answerTypeToFieldType: Record<AnswerTypeId, FormField['type']> = {
    short_answer: 'text',
    long_answer: 'textarea',
    single_choice: 'radio',
    multiple_choice: 'checkbox'
};

const questionTypeIcon = {
    short_answer: faEquals,
    long_answer: faAlignLeft,
    single_choice: faCircleDot,
    multiple_choice: faSquareCheck
};

const getAnswerTypeFromFieldType = (t: FormField['type']): AnswerTypeId => fieldTypeToAnswerType[t] ?? 'short_answer';

const makeDefaultOptions = () => [
    { label: 'Option 1', position: 0, active: true },
    { label: 'Option 2', position: 1, active: true }
];

const AddEditForm: React.FC<Props> = ({
    isActive,
    form,
    mode = 'add',
    isLoading = false,
    assignServiceId,
    serviceCategory,
    onClose,
    onSaved
}) => {
    const dispatch = useDispatch();
    const masterData = useMasterData();
    const questionTypes = masterData.questionTypes;
    const apiClientWrapper = useApiClientWrapper() as any;
    const [formData, setFormData] = useState<Form>(normalizeIncomingForm(form, mode));
    const [isSaving, setIsSaving] = useState(false);
    const [showExitPrompt, setShowExitPrompt] = useState(false);
    const [questionTypeMenuOpen, setQuestionTypeMenuOpen] = useState(false);
    // Dropdown for selecting question type is temporarily disabled for MVP
    const [savedForm, setSavedForm] = useState<Form | null>(null);
    const [pendingManualRule, setPendingManualRule] = useState(false);
    const [step, setStep] = useState<1 | 2>(1);
    const [servicesCategory, setServicesCategory] = useState<FormServiceCategory | undefined>(serviceCategory);

    const incomingServicesKey = [...(form.serviceIds || []), ...(form.services || []).map(service => service.id)].join(
        ','
    );

    useEffect(() => {
        if (isSaving || isLoading) return;
        setFormData(normalizeIncomingForm(form, mode));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading, form.id, form.uuid, mode, incomingServicesKey]);

    useEffect(() => {
        if (!isActive) {
            setIsSaving(false);
            setStep(1);
            setServicesCategory(serviceCategory);
        }
    }, [isActive]);

    const handleInputChange = <K extends keyof Form>(field: K, value: Form[K]) =>
        setFormData(prev => ({ ...prev, [field]: value }));

    const visibleOptionsCount = (f: FormField) => (f.options || []).filter(o => !o._destroy).length;

    const addQuestionByAnswerType = (answerType: AnswerTypeId) => {
        const fieldType = answerTypeToFieldType[answerType] ?? 'text';
        addQuestion(fieldType);
        setQuestionTypeMenuOpen(false);
    };

    const addQuestion = (type: FormField['type']) => {
        const base: FormField = {
            id: `field_${Date.now()}`,
            type,
            answerType: getAnswerTypeFromFieldType(type),
            label: '',
            required: true,
            category: 'for_pets'
        };

        const newField: FormField =
            type === 'radio' || type === 'checkbox'
                ? {
                      ...base,
                      options: [
                          { tempId: makeTempId(), label: 'Option 1', position: 0, active: true },
                          { tempId: makeTempId(), label: 'Option 2', position: 1, active: true }
                      ]
                  }
                : base;

        setFormData(prev => ({ ...prev, fields: [...(prev.fields ?? []), newField] }));
    };

    type FieldKey = keyof FormField | 'audience';
    const handleFieldChange = (index: number, field: FieldKey, value: unknown) => {
        setFormData(prev => ({
            ...prev,
            fields: (((prev as any).fields ?? []) as FormField[]).map((f: FormField, i: number) =>
                i === index ? ({ ...f, [field]: value } as FormField) : f
            )
        }));
    };

    const makeTempId = () => `tmp_${Date.now()}_${Math.random().toString(16).slice(2)}`;

    const addOption = (fieldIndex: number) => {
        setFormData(prev => ({
            ...prev,
            fields: prev.fields.map((f, i) => {
                if (i !== fieldIndex) return f;

                if (f.type !== 'radio' && f.type !== 'checkbox') return f;

                const existing = (f.options || []).filter(o => !o._destroy);
                if (existing.length >= MAX_OPTIONS_PER_QUESTION) return f;

                const nextPos = existing.length;

                const next: FieldOption = {
                    tempId: makeTempId(),
                    label: `Option ${nextPos + 1}`,
                    position: nextPos,
                    active: true
                };

                return { ...f, options: [...(f.options || []), next] };
            })
        }));
    };

    const updateOption = (fieldIndex: number, optionIndex: number, label: string) => {
        setFormData(prev => ({
            ...prev,
            fields: prev.fields.map((f, i) => {
                if (i !== fieldIndex) return f;

                const options = (f.options || []).map((o, j) => (j === optionIndex ? { ...o, label } : o));
                return { ...f, options };
            })
        }));
    };

    const removeOption = (fieldIndex: number, optToRemove: FieldOption) => {
        setFormData(prev => ({
            ...prev,
            fields: prev.fields.map((f, i) => {
                if (i !== fieldIndex) return f;

                const options = f.options || [];
                if (!options.length) return f;

                const realIndex = options.findIndex(o => {
                    if (optToRemove.id != null && o.id != null) return String(o.id) === String(optToRemove.id);
                    if (optToRemove.tempId && o.tempId) return o.tempId === optToRemove.tempId;
                    return false;
                });

                if (realIndex < 0) return f;

                const target = options[realIndex];

                const updated: FieldOption[] = target.id
                    ? options.map((o, j) => (j === realIndex ? { ...o, _destroy: true } : o))
                    : options.filter((_, j) => j !== realIndex);

                const visible = updated.filter(o => !o._destroy);

                const normalized = updated.map(o => {
                    if (o._destroy) return o;
                    const newPos = visible.indexOf(o);
                    return { ...o, position: newPos };
                });

                return { ...f, options: normalized };
            })
        }));
    };

    const removeField = (index: number) =>
        setFormData(prev => {
            const fields = ((prev as any).fields ?? []) as any[];
            const target = fields[index];
            if (!target) return prev;
            // If the field is persisted (id does not start with our temporary prefix), mark it for destruction
            if (target && String(target.id).indexOf('field_') !== 0) {
                const updated = fields.map((f, i) => (i === index ? { ...f, _destroy: true } : f));
                return { ...prev, fields: updated };
            }
            // Otherwise remove temporary field completely
            return { ...prev, fields: fields.filter((_: any, i: number) => i !== index) };
        });

    // Validation
    const hasValidTitle = useMemo(() => formData.title?.trim().length > 0, [formData.title]);
    const hasAtLeastOneValidQuestion = useMemo(() => {
        const isFieldValid = (f: FormField) => {
            const labelOk = (f.label || '').trim().length > 0;

            if (f.type === 'radio' || f.type === 'checkbox') {
                const opts = (f.options || [])
                    .filter(o => !o._destroy)
                    .map(o => (o.label || '').trim())
                    .filter(Boolean);

                return labelOk && opts.length >= 2;
            }

            return labelOk;
        };

        const fields = Array.isArray((formData as any)?.fields) ? (formData as any).fields : [];
        const visible = fields.filter((f: any) => !f._destroy);
        return visible.some(isFieldValid);
    }, [formData.fields]);

    const readOnly = mode === 'view';
    const canGoToNext = hasValidTitle && hasAtLeastOneValidQuestion;
    const canSave = !readOnly && canGoToNext && !isSaving;

    const toSaveForm = (f: Form): SaveForm => {
        const fields = ((f as any).fields ?? []) as FormField[];

        const customQuestions = fields.map((ff: FormField, idx: number) => {
            const persisted = ff.id && !String(ff.id).startsWith('field_');

            const base: any = {
                id: persisted ? ff.id : undefined,
                marketplaceId: String(f.marketplaceId),
                questionText: ff.label,
                required: !!ff.required,
                enabled: true,
                displayOrder: idx,
                category: ff.category ?? 'for_pets',
                answerType: ff.answerType
            };

            if ((ff as any)._destroy) base._destroy = true;

            if ((ff.type === 'radio' || ff.type === 'checkbox') && Array.isArray(ff.options)) {
                base.options = ff.options
                    .map((opt, j) => ({
                        id: opt.id,
                        label: (opt.label || '').trim(),
                        position: typeof opt.position === 'number' ? opt.position : j,
                        active: opt.active ?? true,
                        _destroy: opt._destroy ? true : undefined
                    }))
                    .filter(o => o._destroy || (o.label && o.label.length > 0));
            }

            return base;
        });

        const payload: SaveForm = {
            marketplaceId: String(f.marketplaceId),
            title: f.title,
            active: f.active,
            sentVia: SentVia.Email,
            sendCondition: (f.sendCondition as SendCondition) || SendCondition.Manually,
            uuid: mode === 'edit' ? f.uuid : '',
            customQuestions,
            serviceIds: shouldSelectServices(f.sendCondition)
                ? Array.from(
                      new Set(
                          [...(f.serviceIds || []).map(Number), ...(assignServiceId ? [assignServiceId] : [])].filter(
                              id => Number.isFinite(id) && id > 0
                          )
                      )
                  )
                : []
        };

        return payload;
    };

    const shouldSelectServices = (sendCondition?: string) => isAutomaticSendCondition(sendCondition);

    const applySendCondition = (sendCondition: SendCondition) => {
        setFormData(prev => ({
            ...prev,
            sendCondition,
            serviceIds: shouldSelectServices(sendCondition) ? prev.serviceIds || [] : []
        }));
    };

    const handleSendConditionChange = (sendCondition: SendCondition) => {
        if (
            sendCondition === SendCondition.Manually &&
            isAutomaticSendCondition(formData.sendCondition) &&
            (formData.serviceIds || []).length > 0
        ) {
            setPendingManualRule(true);
            return;
        }
        applySendCondition(sendCondition);
    };

    const getErrorMessages = (errorsArray?: any[]) => {
        if (!errorsArray || errorsArray.length === 0) return 'Error sending form.';
        return errorsArray.join(', ');
    };

    const handleSave = async () => {
        if (!canSave || isSaving) return;
        try {
            setIsSaving(true);
            const payload = toSaveForm(formData);
            const saved =
                mode === 'edit'
                    ? await (dispatch as any)(updateFormThunk(apiClientWrapper, String(formData.id), payload))
                    : await (dispatch as any)(createFormThunk(apiClientWrapper, payload));

            const savedFormWithPayload = {
                ...(saved as Form),
                sendCondition: payload.sendCondition || (saved as Form).sendCondition,
                serviceIds: Array.from(
                    new Set([...(((saved as Form).serviceIds as number[]) || []), ...(payload.serviceIds || [])])
                )
            };

            setSavedForm(savedFormWithPayload);
            onSaved(savedFormWithPayload);
        } catch (err) {
            const errors = (err as any)?.response?.data?.errors;
            const errorMessage = getErrorMessages(errors);
            dispatch(showErrorThunk(errorMessage));
        } finally {
            setIsSaving(false);
        }
    };

    const typeLabel = (t: FormField['type']) =>
        t === 'text'
            ? 'Short Answer'
            : t === 'textarea'
            ? 'Long Answer'
            : t === 'radio'
            ? 'Single Choice'
            : 'Multiple Choice';

    const typeIcon = (t: FormField['type']) =>
        t === 'text' ? faEquals : t === 'textarea' ? faAlignLeft : t === 'radio' ? faCircleDot : faCheckSquare;

    const renderQuestionsStep = (
        <div className={styles.section}>
            <div className={styles.formRow}>
                <div className={styles.formField}>
                    <label>
                        Form Name
                        <span className={styles.requiredMark}>*</span>
                    </label>
                    <Input
                        value={formData.title}
                        onChange={e => handleInputChange('title', e.target.value)}
                        placeholder="Ex. New Boarding Clients"
                        disabled={readOnly}
                    />
                </div>
            </div>
            <div className={styles.customSection}>
                <h3 className={styles.customTitle}>Custom Questions</h3>
                <p className={styles.customDescription}>
                    Create personalized questions to gather the information you need from clients & pets.
                </p>
                <div className={styles.actionsColumn}>
                    {/* Question List */}
                    <div className={styles.questionList}>
                        {(((formData as any).fields ?? []) as any[]).map((field: any, idx: number) => {
                            if (field._destroy) return null; // hide deleted questions
                            return (
                                <div
                                    key={field.id || idx}
                                    className={styles.fieldCard}
                                >
                                    <div className={styles.questionHeader}>
                                        <FontAwesomeIcon icon={typeIcon(field.type)} />
                                        <div>{typeLabel(field.type)}</div>
                                    </div>

                                    <div className={styles.questionBody}>
                                        <div className={styles.formRow}>
                                            <div className={styles.formField}>
                                                <Textarea
                                                    value={field.label}
                                                    onChange={e => handleFieldChange(idx, 'label', e.target.value)}
                                                    placeholder="New Question"
                                                    className={styles.questionInput}
                                                    disabled={readOnly}
                                                    rows={3}
                                                    maxLength={1000}
                                                />
                                            </div>
                                        </div>

                                        {(field.type === 'radio' || field.type === 'checkbox') && (
                                            <div className={styles.fieldOptions}>
                                                {(field.options || [])
                                                    .filter((option: FieldOption) => !option._destroy)
                                                    .slice()
                                                    .sort(
                                                        (a: FieldOption, b: FieldOption) =>
                                                            (a.position ?? 0) - (b.position ?? 0)
                                                    )
                                                    .map((opt: FieldOption, j: number) => (
                                                        <div
                                                            key={opt.id ?? opt.tempId}
                                                            className={styles.optionRow}
                                                        >
                                                            <div className={styles.optionCol}>
                                                                <label className={styles.optionLabel}>
                                                                    Answer {j + 1}
                                                                </label>
                                                                <Input
                                                                    value={opt.label}
                                                                    onChange={e => updateOption(idx, j, e.target.value)}
                                                                    placeholder={`New Answer ${j + 1}`}
                                                                    className={styles.answerInput}
                                                                    disabled={readOnly}
                                                                />
                                                            </div>
                                                            {!readOnly && (
                                                                <Button
                                                                    label=""
                                                                    size="small"
                                                                    onlyIcon={true}
                                                                    leftIcon={faTrashAlt}
                                                                    className={`${styles.optionDeleteBtn} ${styles.optionDeleteBtnWithMargin}`}
                                                                    onClick={() => removeOption(idx, opt)}
                                                                />
                                                            )}
                                                        </div>
                                                    ))}

                                                {!readOnly && (
                                                    <Button
                                                        label="Add Next Answer"
                                                        size="small"
                                                        variant="text-green"
                                                        leftIcon={faPlus}
                                                        onClick={() => addOption(idx)}
                                                        disabled={
                                                            visibleOptionsCount(field) >= MAX_OPTIONS_PER_QUESTION
                                                        }
                                                    />
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    <div className={styles.questionFooter}>
                                        <div className={styles.audienceGroup}>
                                            <label className={styles.radioLabel}>
                                                <input
                                                    type="radio"
                                                    name={`aud_${idx}`}
                                                    checked={(field.category || 'for_pets') === 'for_pets'}
                                                    onChange={() => handleFieldChange(idx, 'category', 'for_pets')}
                                                    disabled={readOnly}
                                                />
                                                For Pets
                                            </label>
                                            <label className={styles.radioLabel}>
                                                <input
                                                    type="radio"
                                                    name={`aud_${idx}`}
                                                    checked={field.category === 'for_customers'}
                                                    onChange={() => handleFieldChange(idx, 'category', 'for_customers')}
                                                    disabled={readOnly}
                                                />
                                                For Clients
                                            </label>
                                        </div>

                                        <div className={styles.footerRight}>
                                            <div className={styles.radioLabel}>
                                                <Switch
                                                    checked={!!field.required}
                                                    onChange={checked => handleFieldChange(idx, 'required', checked)}
                                                    disabled={readOnly}
                                                />
                                                <span>Required</span>
                                            </div>
                                            {!readOnly && (
                                                <div className={styles.iconButtons}>
                                                    <Button
                                                        label=""
                                                        leftIcon={faCopy}
                                                        size="small"
                                                        variant="outlined"
                                                        className={styles.optionDeleteBtn}
                                                        onlyIcon={true}
                                                        onClick={() =>
                                                            setFormData(prev => {
                                                                const copy = [...((prev as any).fields ?? [])];
                                                                copy.splice(idx + 1, 0, {
                                                                    ...field,
                                                                    id: `field_${Date.now()}`
                                                                });
                                                                return { ...prev, fields: copy };
                                                            })
                                                        }
                                                    ></Button>
                                                    <Button
                                                        label=""
                                                        leftIcon={faTrashAlt}
                                                        variant="outlined"
                                                        size="small"
                                                        onlyIcon={true}
                                                        className={styles.optionDeleteBtn}
                                                        onClick={() => removeField(idx)}
                                                    ></Button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Add Question under the list, as per design */}
                    {!readOnly && (
                        <div className={styles.menuWrapper}>
                            <Dropdown
                                open={questionTypeMenuOpen}
                                onOpenChange={setQuestionTypeMenuOpen}
                            >
                                <Button
                                    label="Add Question"
                                    variant="green"
                                    rightIcon={questionTypeMenuOpen ? faChevronUp : faChevronDown}
                                    onClick={() => setQuestionTypeMenuOpen(open => !open)}
                                />

                                {questionTypeMenuOpen && (
                                    <div className={styles.dropdownMenu}>
                                        {(questionTypes || []).map((questionType: Option<string>) => (
                                            <Button
                                                key={questionType.id}
                                                label={questionType.name}
                                                variant="text-green"
                                                className={styles.dropdownItem}
                                                leftIcon={questionTypeIcon[questionType.id as AnswerTypeId]}
                                                onClick={() => addQuestionByAnswerType(questionType.id as AnswerTypeId)}
                                            />
                                        ))}
                                    </div>
                                )}
                            </Dropdown>
                        </div>
                    )}

                    <div className={styles.saveButton}>
                        <Button
                            label="Next"
                            variant="primary"
                            disabled={!readOnly && !canGoToNext}
                            noColorOnDisabled
                            onClick={() => setStep(2)}
                        />
                    </div>
                </div>
            </div>
        </div>
    );

    const renderSendingStep = (
        <div className={styles.section}>
            <FormSendingRules
                value={formData.sendCondition as SendCondition}
                disabled={readOnly}
                onChange={handleSendConditionChange}
            />
            <div
                className={!shouldSelectServices(formData.sendCondition) ? styles.servicesHidden : undefined}
                aria-hidden={!shouldSelectServices(formData.sendCondition)}
            >
                <FormServiceSelector
                    selectedIds={formData.serviceIds || []}
                    lockedIds={assignServiceId ? [assignServiceId] : []}
                    initialCategory={servicesCategory || serviceCategory}
                    associatedServices={formData.services}
                    disabled={readOnly || !shouldSelectServices(formData.sendCondition)}
                    onChange={serviceIds => handleInputChange('serviceIds', serviceIds)}
                    onCategoryChange={setServicesCategory}
                />
            </div>
            {!readOnly && (
                <div className={styles.stepActions}>
                    <Button
                        label={isSaving ? 'Saving...' : 'Save'}
                        variant="green"
                        size="inline"
                        className={styles.saveAction}
                        disabled={!canSave}
                        noColorOnDisabled
                        isLoading={isSaving}
                        onClick={handleSave}
                    />
                </div>
            )}
        </div>
    );

    const renderSuccess = (
        <SuccessScreen
            title={mode === 'edit' ? 'Updated Successfully' : 'Good Job'}
            message={
                <p className={styles.successSubtitle}>
                    <strong>{savedForm?.title || '[Form name]'}</strong> was {mode === 'edit' ? 'updated' : 'added'}{' '}
                    successfully.
                </p>
            }
            buttonLabel="Go to Forms"
            buttonVariant="black"
            onClick={onClose}
        />
    );

    if (!isActive) return null;

    if (isLoading) {
        return (
            <OverFullWindow
                title={mode === 'edit' ? 'Edit Form' : mode === 'view' ? 'View Form' : 'Adding Form'}
                onClose={() => (readOnly || savedForm ? onClose() : setShowExitPrompt(true))}
                showMobileLogo
                centered
                detailContainerClassName={styles.detailContainer}
            >
                <div className={styles.container}>
                    <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
                        <Spinner />
                    </div>
                </div>
            </OverFullWindow>
        );
    }

    return (
        <OverFullWindow
            title={mode === 'edit' ? 'Edit Form' : mode === 'view' ? 'View Form' : 'Adding Form'}
            onClose={() => (readOnly || savedForm ? onClose() : setShowExitPrompt(true))}
            onHeaderBack={step === 2 && !savedForm ? () => setStep(1) : undefined}
            showMobileLogo
            centered
            detailContainerClassName={styles.detailContainer}
        >
            {!readOnly && showExitPrompt && (
                <ExitWithoutSavingModal
                    onDiscard={() => onClose()}
                    onKeepEditing={() => setShowExitPrompt(false)}
                />
            )}
            {pendingManualRule && (
                <GenericDeleteModal
                    title={<span>Change sending rule?</span>}
                    body={<p>Changing to Manually will remove the services associated with this form.</p>}
                    onCancel={() => setPendingManualRule(false)}
                    onConfirm={() => {
                        applySendCondition(SendCondition.Manually);
                        setPendingManualRule(false);
                    }}
                />
            )}
            <div className={`${styles.container} ${step === 2 && !savedForm ? styles.sendingStep : ''}`}>
                {savedForm ? (
                    renderSuccess
                ) : (
                    <>
                        <div
                            className={step !== 1 ? styles.stepHidden : undefined}
                            aria-hidden={step !== 1}
                        >
                            {renderQuestionsStep}
                        </div>
                        <div
                            className={step !== 2 ? styles.stepHidden : undefined}
                            aria-hidden={step !== 2}
                        >
                            {renderSendingStep}
                        </div>
                    </>
                )}
            </div>
        </OverFullWindow>
    );
};

export default AddEditForm;
